import '../../_common/components/timestamp-dock';

import type { BlockComponent, UIEventHandler, EditorHost } from '@blocksuite/block-std';
import type { AffineDragHandleWidget } from '@blocksuite/blocks';
import type { BlockModel } from '@blocksuite/store';
import { html, nothing } from 'lit';
import { styleMap } from 'lit/directives/style-map.js';

interface DragHoverRect {
  top: number;
  left: number;
  width: number;
  height: number;
}

interface DragHandleInternals {
  host: EditorHost;
  requestUpdate: () => void;
  _isAIButtonClicked: boolean;
  _showDragHandleOnHoverBlock: (blockPath: string) => void;
  _clickHandler: UIEventHandler;
  _getBlockComponentFromViewStore: (blockPath: string) => BlockComponent | null;
  _getDraggingAreaRect: (block: BlockComponent) => DOMRect | null;
  _dragHoverRect: DragHoverRect | null;
  _anchorBlockId: string;
  _updateDragHoverRect: (blocks: BlockComponent[]) => void;
  render: () => unknown;
}

function getValidBlock(host: EditorHost | null, blockId: string): BlockModel | null {
  if (!host?.doc || !blockId) return null;
  try {
    return host.doc.getBlockById(blockId) ?? null;
  } catch {
    return null;
  }
}

export function setupDragHandleEntry(widget: AffineDragHandleWidget) {
  const dragHandle = widget as unknown as DragHandleInternals;
  // Track whether AI button was clicked to control highlight behavior.
  dragHandle._isAIButtonClicked = false;

  // Preserve original references for methods we plan to override.
  const originalShowDragHandle = dragHandle._showDragHandleOnHoverBlock;
  const originalClickHandler = dragHandle._clickHandler;

  /**
   * Override: Show the drag handle at a custom position and update hover rect.
   */
  dragHandle._showDragHandleOnHoverBlock = function (blockPath: string) {
    originalShowDragHandle.call(this, blockPath);

    const block = this._getBlockComponentFromViewStore(blockPath);
    if (!block) return;

    this._updateDragHoverRect([block]);
  };

  /**
   * Utility: Update the hover rect for multiple blocks.
   */
  dragHandle._updateDragHoverRect = function (blocks: BlockComponent[]) {
    if (!blocks || blocks.length === 0) return;

    let minTop = Infinity;
    let maxBottom = -Infinity;
    let minLeft = Infinity;
    let maxRight = -Infinity;

    blocks.forEach(block => {
      const rect = this._getDraggingAreaRect(block);
      if (!rect) return;
      minTop = Math.min(minTop, rect.top);
      maxBottom = Math.max(maxBottom, rect.bottom);
      minLeft = Math.min(minLeft, rect.left);
      maxRight = Math.max(maxRight, rect.right);
    });

    this._dragHoverRect = {
      top: minTop,
      left: minLeft,
      width: maxRight - minLeft,
      height: maxBottom - minTop,
    };

    this.requestUpdate();
  };

  dragHandle._clickHandler = function (ctx: Parameters<UIEventHandler>[0]) {
    const state = ctx.get('pointerState');
    const target = state.raw.target as HTMLElement;
    if (target.closest('timestamp-dock')) {
      state.raw.stopPropagation();
      state.raw.preventDefault();
      return;
    }
    originalClickHandler.call(this, ctx);
  };

  dragHandle.render = function () {
    const blockId = this._anchorBlockId;
    const block = getValidBlock(this.host, blockId);

    const timestampStyle = styleMap({
      position: 'absolute',
      top: this._dragHoverRect ? `${this._dragHoverRect.top + 4}px` : '0',
      left: this._dragHoverRect ? `${this._dragHoverRect.left - 50}px` : '0',
      zIndex: '999',
    });

    return html`
      <div class="affine-drag-handle-widget">
        <div class="affine-drag-handle-container">
          <div class="affine-drag-handle-grabber"></div>
        </div>
        ${block
          ? html`<div style=${timestampStyle}>
              <timestamp-dock
                .host=${this.host}
                .dragHandle=${this}
                .blockId=${blockId}
                @click=${(e: Event) => {
                  e.stopPropagation();
                  e.preventDefault();
                }}
                @mousemove=${(e: Event) => e.stopPropagation()}
              ></timestamp-dock>
            </div>`
          : nothing}
      </div>
    `;
  };
}
